import type { ReactNode } from "react";
import type { CustomerSummary, DashboardStats } from "../types";
import { customerStatus, fmtTsShort } from "../utils";
import { MaskedRevenue } from "./MaskedRevenue";

function StatCard({
  label,
  value,
  sub,
  color,
}: {
  label: string;
  value: ReactNode;
  sub?: string;
  color: "indigo" | "emerald" | "amber" | "slate" | "rose" | "sky";
}) {
  const colors = {
    indigo: "text-indigo-600",
    emerald: "text-emerald-600",
    amber: "text-amber-600",
    slate: "text-slate-600",
    rose: "text-rose-600",
    sky: "text-sky-600",
  };
  const bg = {
    indigo: "bg-indigo-50 border-indigo-100",
    emerald: "bg-emerald-50 border-emerald-100",
    amber: "bg-amber-50 border-amber-100",
    slate: "bg-slate-50 border-slate-200",
    rose: "bg-rose-50 border-rose-100",
    sky: "bg-sky-50 border-sky-100",
  };

  return (
    <div className={`rounded-lg border px-4 py-3 ${bg[color]}`}>
      <div className="text-xs font-medium text-ink-500">{label}</div>
      <div className={`mt-1 tabular-nums ${colors[color]}`}>
        {typeof value === "string" || typeof value === "number" ? (
          <span className="text-2xl font-semibold">{value}</span>
        ) : (
          value
        )}
      </div>
      {sub && <div className="mt-0.5 text-xs text-ink-400">{sub}</div>}
    </div>
  );
}

export function Dashboard({
  stats,
  customers,
  onSelect,
}: {
  stats: DashboardStats | null;
  customers: CustomerSummary[];
  onSelect: (id: string) => void;
}) {
  if (!stats) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-ink-500">
        加载统计中…
      </div>
    );
  }

  const paidList = customers.filter((c) => customerStatus(c) === "paid");
  const trialList = customers.filter((c) => customerStatus(c) === "trial");

  return (
    <div className="scrollbar-thin h-full overflow-auto p-6">
      <div className="mx-auto max-w-4xl">
        <h2 className="text-lg font-semibold text-ink-800">仪表盘</h2>
        <p className="mt-0.5 text-sm text-ink-500">客户付费与试用概况</p>

        <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          <StatCard label="总客户" value={stats.total_customers} color="indigo" />
          <StatCard label="付费客户" value={stats.paid_customers} color="emerald" sub="成交价 > 0" />
          <StatCard label="试用客户" value={stats.trial_customers} color="amber" sub="已签发、未成交" />
          <StatCard label="待跟进" value={stats.prospect_customers} color="slate" sub="尚无 license" />
          <StatCard
            label="累计营收"
            value={<MaskedRevenue amount={stats.revenue_cny} className="text-emerald-600" size="lg" />}
            color="emerald"
          />
          <StatCard label="有效 license" value={stats.active_licenses} color="sky" />
          <StatCard label="即将过期" value={stats.expiring_soon} color="rose" sub="7 天内" />
          <StatCard label="已过期" value={stats.expired_licenses} color="slate" />
        </div>

        <div className="mt-8 grid gap-4 lg:grid-cols-2">
          <CustomerGroup
            title="付费客户"
            titleColor="text-emerald-700"
            dotColor="bg-emerald-500"
            items={paidList}
            onSelect={onSelect}
            empty="暂无付费客户"
          />
          <CustomerGroup
            title="试用客户"
            titleColor="text-amber-700"
            dotColor="bg-amber-500"
            items={trialList}
            onSelect={onSelect}
            empty="暂无试用客户"
          />
        </div>
      </div>
    </div>
  );
}

function CustomerGroup({
  title,
  titleColor,
  dotColor,
  items,
  onSelect,
  empty,
}: {
  title: string;
  titleColor: string;
  dotColor: string;
  items: CustomerSummary[];
  onSelect: (id: string) => void;
  empty: string;
}) {
  return (
    <div className="card">
      <div className="flex items-center gap-2 border-b border-ink-100 px-4 py-2.5">
        <span className={`h-2 w-2 rounded-full ${dotColor}`} />
        <h3 className={`text-sm font-semibold ${titleColor}`}>
          {title}
          <span className="ml-1.5 font-normal text-ink-400">({items.length})</span>
        </h3>
      </div>
      {items.length === 0 ? (
        <div className="px-4 py-6 text-center text-sm text-ink-400">{empty}</div>
      ) : (
        <ul className="divide-y divide-ink-50">
          {items.map((c) => (
            <li key={c.id}>
              <button
                className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm hover:bg-ink-50"
                onClick={() => onSelect(c.id)}
              >
                <span className="min-w-0 flex-1 truncate font-medium">{c.name || c.id}</span>
                {typeof c.price_cny === "number" && c.price_cny > 0 && (
                  <span className="shrink-0 font-medium text-emerald-600">¥{c.price_cny}</span>
                )}
                <span className="shrink-0 text-xs text-ink-400">{fmtTsShort(c.updated_at)}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
